import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Video, X, Send, Circle, Square, Loader2 } from 'lucide-react';
import VideoRecorder from '../lib/VideoRecorder';

/**
 * VideoMessageRecorder - Video message recording UI component
 * Inspired by Rocket.Chat's VideoMessageRecorder
 */
const VideoMessageRecorder = ({ onRecordingComplete, onCancel }) => {
  const [state, setState] = useState('idle'); // 'idle' | 'preview' | 'recording' | 'stopped' | 'loading'
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);

  const recorderRef = useRef(null);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  // Format duration as MM:SS
  const formatDuration = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Start the timer
  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      if (recorderRef.current) {
        setDuration(recorderRef.current.getDuration());
      }
    }, 100);
  }, []);

  // Stop the timer
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Initialize camera preview on mount
  useEffect(() => {
    const initCamera = async () => {
      if (!VideoRecorder.isSupported()) {
        setError('Video recording is not supported in this browser');
        return;
      }

      try {
        recorderRef.current = new VideoRecorder();
        await recorderRef.current.startPreview(videoRef.current);
        setState('preview');
      } catch (err) {
        console.error('Failed to start camera:', err);
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setError('Camera permission denied. Please allow access to record video.');
        } else {
          setError(err.message || 'Failed to access camera');
        }
      }
    };

    initCamera();

    return () => {
      stopTimer();
      if (recorderRef.current) {
        recorderRef.current.cancel();
      }
    };
  }, [stopTimer]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    stopTimer();
    if (recorderRef.current) {
      recorderRef.current.cancel();
      recorderRef.current = null;
    }
    setState('idle');
    setDuration(0);
    if (onCancel) {
      onCancel();
    }
  }, [stopTimer, onCancel]);

  // Start recording
  const handleStartRecording = () => {
    if (!recorderRef.current || state !== 'preview') return;

    try {
      recorderRef.current.startRecording();
      setState('recording');
      setDuration(0);
      startTimer();
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError(err.message || 'Failed to start recording');
    }
  };

  // Stop recording
  const handleStopRecording = async () => {
    if (!recorderRef.current || state !== 'recording') return;

    stopTimer();
    setState('stopped');
  };

  // Send the recording
  const handleSend = async () => {
    if (!recorderRef.current) return;

    setState('loading');

    try {
      const result = await recorderRef.current.stopRecording();

      if (result.blob && result.blob.size > 0) {
        const filename = `Video_message_${Date.now()}.${result.extension}`;
        const file = new File([result.blob], filename, { type: result.mimeType });

        if (onRecordingComplete) {
          onRecordingComplete({
            file,
            blob: result.blob,
            mimeType: result.mimeType,
            duration: result.duration,
            filename,
          });
        }
      }

      // Cleanup
      if (recorderRef.current) {
        recorderRef.current.cleanup();
        recorderRef.current = null;
      }
    } catch (err) {
      console.error('Failed to save video:', err);
      setError(err.message || 'Failed to save video');
      setState('stopped');
    }
  };

  // Toggle recording
  const handleToggleRecording = () => {
    if (state === 'preview') {
      handleStartRecording();
    } else if (state === 'recording') {
      handleStopRecording();
    }
  };

  // Render error state
  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-sm mx-4">
          <div className="text-center">
            <Video className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <div className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden max-w-md w-full mx-4 shadow-2xl">
        {/* Video preview */}
        <div className="relative bg-black aspect-video">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }} // Mirror for selfie view
          />

          {/* Recording indicator */}
          {state === 'recording' && (
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/50 px-3 py-1.5 rounded-full">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
              <span className="text-white text-sm font-mono">{formatDuration(duration)}</span>
            </div>
          )}

          {/* Timer for stopped state */}
          {state === 'stopped' && (
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/50 px-3 py-1.5 rounded-full">
              <span className="text-white text-sm font-mono">{formatDuration(duration)}</span>
            </div>
          )}

          {/* Loading overlay */}
          {state === 'loading' && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-white animate-spin" />
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="p-4 flex items-center justify-between bg-slate-50 dark:bg-slate-900">
          {/* Record/Stop button */}
          <button
            type="button"
            onClick={handleToggleRecording}
            disabled={state === 'loading' || state === 'stopped' || state === 'idle'}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
              state === 'recording'
                ? 'bg-red-500 hover:bg-red-600'
                : 'bg-red-500 hover:bg-red-600'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            title={state === 'recording' ? 'Stop recording' : 'Start recording'}
          >
            {state === 'recording' ? (
              <Square className="w-6 h-6 text-white fill-white" />
            ) : (
              <Circle className="w-6 h-6 text-white fill-white" />
            )}
          </button>

          {/* Right side buttons */}
          <div className="flex items-center gap-3">
            {/* Cancel button */}
            <button
              type="button"
              onClick={handleCancel}
              disabled={state === 'loading'}
              className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>

            {/* Send button */}
            <button
              type="button"
              onClick={handleSend}
              disabled={state !== 'stopped' || state === 'loading'}
              className="px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {state === 'loading' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoMessageRecorder;
